import React, { FormEvent, useMemo, useState } from "react";
import {
  Input,
  Button,
  Text,
  makeStyles,
  tokens,
  Field,
} from "@fluentui/react-components";
import {
  BasePasswordFormParamsKeys,
  PasswordFormParams,
} from "../../../types/login";
import { ValidationErrors } from "../../../types/common";
import { createPasswordFormValidation } from "./PasswordForm.validator";
import { validateField, validateForm } from "../Form.validators";
import { ValidatableValue } from "../Form.types";

/* global HTMLFormElement */

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    padding: "12px",
  },
  title: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
});

interface PasswordFormProps {
  error?: string;
  saving?: boolean;
  onClearError: () => void;
  onSubmit: (params: PasswordFormParams) => void;
}

type FormRecord = Record<keyof PasswordFormParams, ValidatableValue>;

const PasswordForm: React.FC<PasswordFormProps> = ({
  error,
  saving,
  onClearError,
  onSubmit,
}) => {
  const styles = useStyles();
  const [formData, setFormData] = useState<PasswordFormParams>({
    password: "",
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );

  const validationConfig = useMemo(() => createPasswordFormValidation(), []);

  const handleChange = (field: BasePasswordFormParamsKeys, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (error) onClearError();

    const fieldConfig = validationConfig[field];
    if (fieldConfig) {
      const result = validateField<string>(value, fieldConfig);
      if (!result.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          [field]: result.error,
        }));
      } else {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validateForm<FormRecord>(
      formData as FormRecord,
      validationConfig,
    );
    setValidationErrors(errors);

    if (Object.keys(errors).length === 0 && !saving) {
      onSubmit(formData);
    }
  };

  const isSaveDisabled = Object.keys(validationErrors).length > 0;

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <Text className={styles.title}>Enter Password</Text>

      <Field
        id="password"
        label="Password"
        validationState={validationErrors.password ? "error" : "none"}
        validationMessage={validationErrors.password}
      >
        <Input
          type="password"
          value={formData.password}
          onChange={(e) => handleChange("password", e.target.value)}
          placeholder="Enter password"
          required
        />
      </Field>

      {error && (
        <Field
          id="apiErrorMessage"
          validationState="error"
          validationMessage={error}
        />
      )}

      <Button
        appearance="primary"
        type="submit"
        disabled={!!error || isSaveDisabled || saving}
      >
        Log in
      </Button>
    </form>
  );
};

export default PasswordForm;
